import type { FC } from "react";

type MappingRow = { name: string; pct?: number };

interface MappingPercentageCardProps {
  rows?: MappingRow[] | null;
  loading?: boolean;
  error?: boolean;
  className?: string;
}

const STYLES = [
  { bg: "bg-emerald-50", pctText: "text-emerald-600", bar: "bg-emerald-500" },
  { bg: "bg-blue-50", pctText: "text-blue-600", bar: "bg-blue-500" },
  { bg: "bg-amber-50", pctText: "text-amber-600", bar: "bg-amber-500" },
];

const MappingPercentageCard: FC<MappingPercentageCardProps> = ({
  rows = [],
  loading = false,
  error = false,
  className = "",
}) => {
  if (loading) {
    return (
      <div className={className}>
        <div className="text-xs text-gray-500">Loading…</div>
      </div>
    );
  }

  if (!Array.isArray(rows) || rows.length === 0) {
    return (
      <div className={className}>
        <div className="h-full flex items-center justify-center text-xs text-gray-400">
          No data
        </div>
        {error && (
          <div className="mt-2 text-xs text-red-500">
            Failed to load mapping percentages.
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={className}>
      <div className="space-y-3">
        {rows.map((row, i) => {
          const style = STYLES[i % STYLES.length];

          const rawPct = Number(row.pct ?? 0);

          const displayPct =
            rawPct > 0 && rawPct < 1
              ? rawPct.toFixed(2).replace(/0+$/, "").replace(/\.$/, "")
              : Math.round(rawPct).toString();

          const barPct = rawPct > 0 ? Math.max(rawPct, 1) : 0;

          return (
            <div key={row.name} className={`${style.bg} rounded-lg p-3`}>
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold text-gray-800">
                  {row.name}
                </span>
                <span className={`text-sm font-semibold ${style.pctText}`}>
                  {displayPct}%
                </span>
              </div>

              <div className="mt-2 h-3 bg-gray-200/80 rounded-full overflow-hidden">
                <div
                  className={`h-full ${style.bar} rounded-full transition-[width] duration-700`}
                  style={{ width: `${barPct}%` }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default MappingPercentageCard;
